import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import AspectRatio from 'components/AspectRatio';
import { Small, MediumUp } from 'components/MediaQuery';
import RichText from 'components/RichText';
import { medium, largeUp, mediumUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';

import BackgroundImage from './BackgroundImage';
import { LAYOUT } from './constants';

const ParentCollectionBanner = ({
  backgroundColor,
  backgroundMobileImage,
  backgroundDesktopImage,
  crossPromotionalDesktopImage,
  crossPromotionalMobileImage,
  isLoading,
  className,
  layout,
  text,
  textAlignment,
  textColor,
  visibility,
}) => {
  if (!visibility.match(/web/i)) {
    return null;
  }

  return (
    <div
      className={classNames('ParentCollectionBanner', className, {
        'ParentCollectionBanner--cross-promotion': layout === LAYOUT.CROSS_PROMOTION,
        'ParentCollectionBanner--loading': isLoading,
      })}
    >
      {layout === LAYOUT.CROSS_PROMOTION && (
        <>
          {crossPromotionalMobileImage && (
            <Small>
              <div className="ParentCollectionBanner__cross-image">
                <AspectRatio
                  width={crossPromotionalMobileImage.details.image.width}
                  height={crossPromotionalMobileImage.details.image.height}
                >
                  <BackgroundImage image={crossPromotionalMobileImage} />
                </AspectRatio>
              </div>
            </Small>
          )}
          {crossPromotionalDesktopImage && (
            <MediumUp>
              <div className="ParentCollectionBanner__cross-image">
                <AspectRatio
                  width={crossPromotionalDesktopImage.details.image.width}
                  height={crossPromotionalDesktopImage.details.image.height}
                >
                  <BackgroundImage image={crossPromotionalDesktopImage} />
                </AspectRatio>
              </div>
            </MediumUp>
          )}
        </>
      )}

      <div className="ParentCollectionBanner__content">
        <RichText content={text} />
      </div>
      {layout === LAYOUT.COLLECTION_PAGE && (
        <>
          {backgroundMobileImage && (
            <Small>
              <div className="ParentCollectionBanner__image">
                <BackgroundImage image={backgroundMobileImage} />
              </div>
            </Small>
          )}
          {backgroundDesktopImage && (
            <MediumUp>
              <div className="ParentCollectionBanner__image">
                <BackgroundImage image={backgroundDesktopImage} />
              </div>
            </MediumUp>
          )}
        </>
      )}
      <style jsx>
        {`
          .ParentCollectionBanner {
            position: relative;
            background-color: ${backgroundColor};
            text-align: ${textAlignment};
            color: ${textColor};
            margin: ${Theme.get('spacing') * 4}px 0;
            width: 100vw;
            opacity: ${isLoading ? 0 : 1};
            transition: opacity 0.3s ease-in-out;
          }
          .ParentCollectionBanner__content {
            padding: ${Theme.get('spacing') * 4}px;
          }
          .ParentCollectionBanner.ParentCollectionBanner--second {
            margin: 0;
          }

          .ParentCollectionBanner--cross-promotion {
            display: flex;
            align-items: stretch;
          }

          .ParentCollectionBanner--cross-promotion .ParentCollectionBanner__content {
            padding: ${Theme.get('spacing') * 3}px;
          }

          .ParentCollectionBanner__cross-image {
            min-width: clamp(100px, 25vw, 45vw);
          }

          .ParentCollectionBanner__content {
            position: relative;
            z-index: 1;
            align-self: center;
          }

          .ParentCollectionBanner__image {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
          }

          @media ${medium} {
            .ParentCollectionBanner.ParentCollectionBanner--second {
              width: calc(100% + ${Theme.get('spacing') * 12}px);
              margin-left: ${Theme.get('spacing') * -8}px;
              margin-right: ${Theme.get('spacing') * -8}px;
            }
          }

          @media ${mediumUp} {
            .ParentCollectionBanner__cross-image {
              min-width: 200px;
            }
            .ParentCollectionBanner {
              width: calc(100% + ${Theme.get('spacing') * 4}px);
              margin: ${Theme.get('spacing') * 4}px ${Theme.get('spacing') * -4}px;
            }

            .ParentCollectionBanner--cross-promotion .ParentCollectionBanner__content {
              padding: ${Theme.get('spacing') * 4}px;
            }
          }

          @media ${largeUp} {
            .ParentCollectionBanner.ParentCollectionBanner--second {
              margin-left: ${Theme.get('spacing') * -4}px;
              margin-right: 0;
              width: calc(100% + ${Theme.get('spacing') * 4}px);
            }
          }
        `}
      </style>
      <style jsx global>
        {`
          .ParentCollectionBanner .RichText > * {
            color: inherit;
          }

          .ParentCollectionBanner .RichText h3.typography--tertiary-heading {
            color: inherit;
            font-size: ${Theme.get('font-size') * 1.125}px;
            margin-bottom: ${Theme.get('spacing') * 1.25}px;
          }
          .ParentCollectionBanner p.Paragraph {
            color: inherit;
            font-size: ${Theme.get('font-size-small-px')};
            margin-bottom: ${Theme.get('spacing') * 1.25}px;
          }
          .ParentCollectionBanner p.Paragraph:last-child {
            margin-bottom: 0;
          }
          .ParentCollectionBanner .Paragraph a {
            color: inherit;
            font-size: ${Theme.get('font-size-small-px')};
          }
          .ParentCollectionBanner .Paragraph a + a {
            margin-left: ${Theme.get('spacing') * 2}px;
          }
        `}
      </style>
    </div>
  );
};

ParentCollectionBanner.propTypes = {
  backgroundColor: PropTypes.string,
  className: PropTypes.string,
  layout: PropTypes.string,
  text: PropTypes.shape({}).isRequired,
  textAlignment: PropTypes.string,
  textColor: PropTypes.string,
  visibility: PropTypes.string,
  backgroundMobileImage: PropTypes.shape({
    url: PropTypes.string.isRequired,
    details: PropTypes.shape({
      image: PropTypes.shape({
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
      }).isRequired,
    }),
  }),
  crossPromotionalMobileImage: PropTypes.shape({
    url: PropTypes.string.isRequired,
    details: PropTypes.shape({
      image: PropTypes.shape({
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
      }).isRequired,
    }),
  }),
  crossPromotionalDesktopImage: PropTypes.shape({
    url: PropTypes.string.isRequired,
    details: PropTypes.shape({
      image: PropTypes.shape({
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
      }).isRequired,
    }),
  }),
  backgroundDesktopImage: PropTypes.shape({
    url: PropTypes.string.isRequired,
    details: PropTypes.shape({
      image: PropTypes.shape({
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
      }).isRequired,
    }),
  }),
  isLoading: PropTypes.bool,
};

ParentCollectionBanner.defaultProps = {
  backgroundColor: '#fff',
  backgroundDesktopImage: null,
  backgroundMobileImage: null,
  crossPromotionalDesktopImage: null,
  crossPromotionalMobileImage: null,
  className: null,
  layout: LAYOUT.COLLECTION_PAGE,
  textAlignment: 'center',
  textColor: 'black',
  visibility: 'web',
  isLoading: false,
};

export default ParentCollectionBanner;
